import { Component, inject, OnDestroy, OnInit } from '@angular/core';

import { MatTableDataSource } from '@angular/material/table';
import { translate } from '@jsverse/transloco';
import moment from 'moment/moment';

import { Subscription } from 'rxjs';
import { Calendar_onValueChangeEvent, CalendarData, DropdownObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from '../../../../app-shared.module';
import { RelativeDateConstants } from '../../../../constants/constants';
import { Account, SemantriaAccount } from '../../../../providers/bridge-api/account.service';
import { AuthService, Session } from '../../../../providers/bridge-api/auth.service';
import {
  StatisticsService,
  UserCreditStatistic,
} from '../../../../providers/semantria-api/statistics.service';
import { DownloadTasksService, TaskType } from '../../../../providers/tasks-api/download.service';
import { AlertService } from '../../../../services/alert.service';
import { DialogService } from '../../../../services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from '../../../../services/otel.service';
import { TimezoneService } from '../../../../services/timezone.service';
import {
  DownloadAsDialogComponent,
  DownloadAsDialogOptions,
  DownloadAsDialogResult,
} from '../../../collection/components/download-as-dialog/download-as-dialog.component';
import {
  DATE_TIME_WITHOUT_TIMEZONE_FORMAT,
  FilterService,
} from '../../../collection/services/filter.service';

@EnableClassTelemetry()
@Component({
  selector: 'credit-usage',
  imports: [AppSharedModule],
  templateUrl: './credit-usage.component.html',
  styleUrls: ['./credit-usage.component.scss'],
  providers: [StatisticsService],
})
export class CreditUsageComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  private session: Session;

  public creditType: DropdownObject[] = [
    { title: translate('credit-usage.assembler-balance'), id: 'ASSEMBLER_BALANCE' },
    { title: translate('credit-usage.driver-analyses'), id: 'DRIVER_ANALYSES' },
    { title: translate('credit-usage.ai-studio'), id: 'SPOT_E_SCENARIO' },
  ];

  displayedColumns = ['email', 'usage'];

  selectedCreditType: string;
  dropdownValue: string;
  calendarData: CalendarData;
  relativeRangeId = RelativeDateConstants.MONTH_TO_DATE_INCLUDE_TODAY;
  startDate: string;
  endDate: string;
  balance: number;
  totalBalance = 0;
  private semantriaAccountSubscription: Subscription;
  account: Account;

  creditUsageData: UserCreditStatistic[] = [];
  dataSource: MatTableDataSource<UserCreditStatistic>;

  private timezoneService = inject(TimezoneService);
  private filterService = inject(FilterService);
  private statisticsService = inject(StatisticsService);
  private alertService = inject(AlertService);
  private authService = inject(AuthService);
  private dialogService = inject(DialogService);
  private semantriaAccount = inject(SemantriaAccount);
  private downloadTaskService = inject(DownloadTasksService);

  constructor() {
    this.dataSource = new MatTableDataSource([]);
    this.calendarData = {
      preselectedCalendarListOptions: this.filterService.generateRelativeRangeOptions(),
    };
    [this.startDate, this.endDate] = this.filterService.computeFixedValueForDateFilter(
      this.relativeRangeId
    );
  }

  async ngOnInit() {
    this.session = await this.authService.getSession();
    this.selectedCreditType = 'SPOT_E_SCENARIO';
    this.semantriaAccountSubscription = this.semantriaAccount.sub.subscribe((account) => {
      if (!account) {
        return;
      }
      this.account = account;
      this.balance = this.account.balances[this.selectedCreditType];
    });

    this.dataSource.data = this.creditUsageData;

    this.creditUsageData = await this.statisticsService.getCreditUsage(
      this.session.info2.account_id,
      this.selectedCreditType,
      this.startDate,
      this.endDate
    );
  }

  ngOnDestroy() {
    this.semantriaAccountSubscription?.unsubscribe();
  }

  onCalendarValueChange(eventDetail: Calendar_onValueChangeEvent): void {
    this.startDate = moment(eventDetail.dates.first)
      .tz(this.timezoneService.timezone, true)
      .format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
    this.endDate = moment(eventDetail.dates.last)
      .tz(this.timezoneService.timezone, true)
      .format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
  }
  changeDisplay(type: string) {
    this.selectedCreditType = type;
    this.dropdownValue = type;
  }

  adjustBalanceTracking() {
    this.creditUsageData.forEach((value) => {
      this.totalBalance += value.values[0].balance_type_decrement_amount;
    });

    this.balance = this.account.balances[this.selectedCreditType];
  }
  async generateReport() {
    this.balance = 0;
    this.totalBalance = 0;
    this.statisticsService
      .getCreditUsage('user', this.selectedCreditType, this.startDate, this.endDate)
      .then((result) => {
        this.creditUsageData = result;
        this.dataSource.data = this.creditUsageData;
        this.adjustBalanceTracking();
      })
      .catch((error) => {
        this.alertService.showErrorToast(translate('credit-usage.failed-to-get'), error);
      });
  }

  downloadReport() {
    this.dialogService
      .showDialog<DownloadAsDialogComponent, DownloadAsDialogOptions>(
        DownloadAsDialogComponent,
        {
          recordCount: this.dataSource.data.length,
          sort: true,
          format: 'xlsx',
          filename: this.selectedCreditType + '_report',
        },
        { width: '600px' },
        false
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const downloadAsResult = result as DownloadAsDialogResult;
          this.downloadTaskService
            .createTask({
              filename: downloadAsResult.fileName,
              _type: TaskType.USAGE_REPORT,
              credit_type: this.selectedCreditType,
              start_date: this.startDate,
              end_date: this.endDate,
              metadata: {
                source: 'Report',
              },
            })
            .then(() => {
              const msg = translate('documents.list.download-started');
              this.alertService.showToast(
                msg,
                0,
                'default',
                false,
                'open-download-center-pointer',
                'open-download-center'
              );
            })
            .catch((error) =>
              this.alertService.showErrorToast(translate('documents.list.download-failed'), error)
            );
        }
      });
  }
}
